import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "background-gallery__chip-tag-container background-gallery__main-chips" }
const _hoisted_2 = { class: "background-gallery__search" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "background-gallery__shadow" }, null, -1)
const _hoisted_4 = { class: "background-gallery__search-chips" }
const _hoisted_5 = { class: "background-gallery__chip-tag-container" }
const _hoisted_6 = { class: "background-gallery__chip-tag-container background-gallery__collection-chips" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "background-gallery__no-results-text" }

import { ref, onBeforeMount, computed, watch } from 'vue'
import PlusIcon from '@/assets/PlusIcon.svg'
import PhotoIcon from '@/assets/PhotoIcon.svg'
import AiIcon from '@/assets/AiIcon.svg'
import AttributionLozenge from '@/components/gallery/AttributionLozenge.vue'
import SearchBar from '@/components/gallery/SearchBar.vue'
import ChipTag from '@/components/gallery/ChipTag.vue'
import FileSelector from '@/components/common/FileSelector.vue'
import Spinner from '@/components/common/Spinner.vue'
import LazyImage from '@/components/gallery/LazyImage.vue'
import LazyVideo from '@/components/gallery/LazyVideo.vue'
import MosaicGrid from '@/components/gallery/MosaicGrid.vue'
import Disclaimer from '@/components/gallery/Disclaimer.vue'
import { storeToRefs } from 'pinia'
import { SearchType, useBackgroundGalleryStore } from '@/store/gallery/background-store'
import { useProjectStore } from '@/store/project-store'
import { useApplicationStore } from '@/store/application-store'
import { backgroundAccept } from '@/static/file-types'
import { GallerySource } from '@/store/gallery/types'
import { SearchState } from '@/types'
import GtmHelper from '@/analytics/gtm-helper'
import { useFileValidator, ValidationError } from '@/composables/UseFileValidator'
import useBackgroundGalleryItem from '@/composables/UseBackgroundGalleryItem'

type GalleryState = 'Gallery' | 'Generator' | 'Search'


export default /*#__PURE__*/_defineComponent({
  __name: 'BackgroundGallery',
  setup(__props) {

_useCssVars(_ctx => ({
  "5e1dc062": (_unref(shadowTop))
}))

const gtmHelper = GtmHelper.getInstance()
const applicationStore = useApplicationStore()

const backgroundGalleryStore = useBackgroundGalleryStore()
const { searchType, selectedCollection, isCollectionsAvailable } = storeToRefs(backgroundGalleryStore)

const projectStore = useProjectStore()

const backgroundGallery = ref<HTMLElement | null>(null)
const backgroundSource = ref<GalleryState | null>(null)
const prevBackgroundSource = ref<GalleryState | null>(null)

const showChips = (state: GalleryState) => backgroundSource.value === state || (backgroundSource.value === 'Generator' && prevBackgroundSource.value === state)
const showContentOutageMsg = computed(() => !isCollectionsAvailable.value && (backgroundSource.value === 'Search' || backgroundSource.value === 'Gallery'))
const isSearching = computed(() => searchState.value === 'searching' || (backgroundGalleryStore.items.length === 0 && searchState.value === 'inactive'))
const showResults = computed(() => backgroundGalleryStore.items.length > 0)
const hasNoResults = computed(() => backgroundGalleryStore.items.length === 0 && searchState.value === 'searched')
const isGiphyDisclaimer = computed(() => (searchState.value !== 'inactive' && searchType.value === SearchType.GIF) || (searchState.value === 'inactive' && selectedCollection.value?.source === 'giphy'))
const disclaimerMsg = computed(() => isGiphyDisclaimer.value ? 'GIPHY content may not be cleared for commercial use' : 'This content is royalty-free')
const placeholderText = computed(() => {
  let text = 'Search millions of videos, GIFs, and photos'
  if (searchState.value !== 'inactive') {
    switch (searchType.value) {
    case SearchType.VIDEO: {
      text = 'Search millions of videos'
      break
    }
    case SearchType.GIF: {
      text = 'Search GIPHY for GIFs'
      break
    }
    case SearchType.PHOTO: {
      text = 'Search millions of photos'
      break
    }
    }
  }
  return text
})

const attribution = computed(() => {
  let source: GallerySource = 'serato'
  if (searchState.value !== 'inactive') {
    source = searchType.value === SearchType.GIF ? 'giphy' : 'pexels'
  } else if (selectedCollection.value) {
    source = selectedCollection.value.source
  }
  return source
})

const { validateBackgroundFile } = useFileValidator()
const { selectBackground, onDragBackground, isSelectedBackground } = useBackgroundGalleryItem()

onBeforeMount(async () => {
  // Fetch Pexel collections metadata and the default media
  await backgroundGalleryStore.fetchCollections()
  await retrieveActiveCollection('Vibes')

  // Preload gallery thumbnails
  if (!applicationStore.isActionMenuLoaded) {
    await Promise.allSettled(backgroundGalleryStore.items.map(item => {
      return fetch(item.preview.imageSource.src)
    }))
    applicationStore.isActionMenuLoaded = true
  }
})

watch(() => applicationStore.isImageGeneratorPanelOpen, () => {
  setGalleryState(applicationStore.isImageGeneratorPanelOpen ? 'Generator' : prevBackgroundSource.value)
})

const setGalleryState = (state: GalleryState | null) => {
  prevBackgroundSource.value = backgroundSource.value
  backgroundSource.value = state === prevBackgroundSource.value ? null : state

  if (state === 'Generator' || prevBackgroundSource.value == 'Generator') {
    return
  }

  if (state !== 'Search') {
    searchState.value = 'inactive'
    phrase.value = ''
  }
  retrieveActiveCollection(backgroundSource.value !== null && selectedCollection.value ? selectedCollection.value.title : 'Vibes')
}

const uploadMedia = async (file: File) => {
  gtmHelper.triggerBackgroundUploadGtmEvent(file.size)
  try {
    await validateBackgroundFile(file)
    projectStore.setBackground(URL.createObjectURL(file), 'fill', file.name)
  } catch (e) {
    if (e instanceof ValidationError) {
      applicationStore.pushSnackbarMessage({
        header: e.title,
        text: e.message,
        state: 'error'
      })
    }
  }
}

const onSelectCollection = (title: string) => {
  if (title !== selectedCollection.value?.title) {
    retrieveActiveCollection(title)
  }
}

const retrieveActiveCollection = (title: string) => {
  const collection = backgroundGalleryStore.collections.find((collection) => collection.title === title)
  if (collection) {
    // Asynchronous call
    backgroundGalleryStore.fetchByCollection(collection)
  } else {
    backgroundGalleryStore.fetchByCollection(backgroundGalleryStore.collections[2]) //selecting trippy collection as the default one.
  }
}

const phrase = ref('')
const searchState = ref<SearchState>('inactive')

const onSearchFocus = () => {
  backgroundSource.value = 'Search'
  if (searchState.value === 'inactive') {
    retrieveDefaultResults()
  }
}

const onSearch = async () => {
  if (backgroundGalleryStore.searchPhrase !== phrase.value) {
    searchState.value = 'searching'
    await backgroundGalleryStore.fetchByKeyword(phrase.value || getDefaultSearchValue())
    searchState.value = 'searched'
    gtmHelper.triggerBackgroundSearchEvent(phrase.value)
  }
}

const onToggleSearchType = async (format: SearchType) => {
  searchType.value = format
  if (phrase.value) {
    searchState.value = 'searching'
    await backgroundGalleryStore.fetchByKeyword(phrase.value)
    searchState.value = 'searched'
  } else {
    // When no search term show default results
    retrieveDefaultResults()
  }
}

const getDefaultSearchValue = () => {
  return searchType.value === SearchType.GIF ? 'vaporwave' : 'music studio'
}

/**
 * Retrieves the default items to display in gallery on starting search
*/
const retrieveDefaultResults = async () => {
  searchState.value = 'searching'
  await backgroundGalleryStore.fetchByKeyword(getDefaultSearchValue())
  searchState.value = 'searched'
}

// Threshold for when to start fetching the next page, might change later
const threshold = 0.9

const onIntersect = (index: number) => {
  if (index === Math.floor((backgroundGalleryStore.items.length - 1) * threshold)) {
    backgroundGalleryStore.nextPage()
  }
}

const shadowTop = computed(() => ['Gallery', null].includes(backgroundSource.value) ? '64px' : '110px')

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "backgroundGallery",
    ref: backgroundGallery,
    class: "background-gallery"
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(FileSelector, {
        accept: _unref(backgroundAccept),
        onFileSelected: uploadMedia
      }, {
        default: _withCtx(() => [
          _createVNode(ChipTag, { text: "Upload" }, {
            default: _withCtx(() => [
              _createVNode(_unref(PlusIcon))
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["accept"]),
      _createVNode(ChipTag, {
        text: "Gallery",
        "is-active": backgroundSource.value === 'Gallery',
        onClick: _cache[0] || (_cache[0] = ($event: any) => (setGalleryState('Gallery')))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(PhotoIcon))
        ]),
        _: 1
      }, 8, ["is-active"]),
      _createVNode(ChipTag, {
        text: "Text to image",
        "is-active": backgroundSource.value === 'Generator',
        onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(applicationStore).isImageGeneratorPanelOpen = true))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(AiIcon))
        ]),
        _: 1
      }, 8, ["is-active"])
    ]),
    _createElementVNode("div", _hoisted_2, [
      _createVNode(SearchBar, {
        placeholder: _unref(placeholderText),
        active: searchState.value !== 'inactive',
        "show-clear-button": searchState.value !== 'inactive',
        onFocus: onSearchFocus,
        onSearch: onSearch,
        onEmptyClear: _cache[2] || (_cache[2] = ($event: any) => (setGalleryState(null))),
        modelValue: phrase.value,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((phrase).value = $event)),
        modelModifiers: { trim: true }
      }, null, 8, ["placeholder", "active", "show-clear-button", "modelValue"])
    ]),
    _hoisted_3,
    _withDirectives(_createVNode(Disclaimer, {
      class: "background-gallery__collection-outage",
      message: 'Some content is currently unavailable'
    }, null, 512), [
      [_vShow, _unref(showContentOutageMsg)]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(Object.values(_unref(SearchType)), (format) => {
          return (_openBlock(), _createBlock(ChipTag, {
            text: format,
            key: `searchType-${format}`,
            "is-active": _unref(searchType) === format,
            onClick: ($event: any) => (onToggleSearchType(format))
          }, null, 8, ["text", "is-active", "onClick"]))
        }), 128))
      ])
    ], 512), [
      [_vShow, showChips('Search')]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_6, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(backgroundGalleryStore).collections, (collection) => {
        return (_openBlock(), _createBlock(ChipTag, {
          text: collection.title,
          key: collection.id,
          "is-active": _unref(selectedCollection)?.title === collection.title && searchState.value === 'inactive',
          onClick: ($event: any) => (onSelectCollection(collection.title))
        }, null, 8, ["text", "is-active", "onClick"]))
      }), 128))
    ], 512), [
      [_vShow, showChips('Gallery')]
    ]),
    _createVNode(Disclaimer, {
      class: "background-gallery__disclaimer",
      message: _unref(disclaimerMsg)
    }, null, 8, ["message"]),
    _withDirectives(_createVNode(Spinner, { class: "background-gallery__spinner" }, null, 512), [
      [_vShow, _unref(isSearching)]
    ]),
    _createVNode(AttributionLozenge, {
      class: "background-gallery__attribution",
      attribution: _unref(attribution)
    }, null, 8, ["attribution"]),
    _withDirectives(_createVNode(MosaicGrid, {
      class: "background-gallery__results",
      items: _unref(backgroundGalleryStore).items,
      "num-cols": 2,
      gap: 4
    }, {
      default: _withCtx(({ item, index }) => [
        _createElementVNode("div", {
          class: _normalizeClass(['background-gallery__results-item', {'background-gallery__results-item--selected': _unref(isSelectedBackground)(item)}]),
          onClick: ($event: any) => (_unref(selectBackground)(item))
        }, [
          (!item.preview.videoSource)
            ? (_openBlock(), _createBlock(LazyImage, {
                key: 0,
                class: "background-gallery__results-item-contents",
                src: item.preview.imageSource.src,
                "parent-container": backgroundGallery.value,
                onViewportIntersect: ($event: any) => (onIntersect(index)),
                onDragstart: ($event: any) => (_unref(onDragBackground)($event, item))
              }, null, 8, ["src", "parent-container", "onViewportIntersect", "onDragstart"]))
            : (_openBlock(), _createBlock(LazyVideo, {
                key: 1,
                class: "background-gallery__results-item-contents",
                draggable: "true",
                src: item.preview.videoSource.src,
                "thumbnail-src": item.preview.imageSource.src,
                "parent-container": backgroundGallery.value,
                onViewportIntersect: ($event: any) => (onIntersect(index)),
                onDragstart: ($event: any) => (_unref(onDragBackground)($event, item))
              }, null, 8, ["src", "thumbnail-src", "parent-container", "onViewportIntersect", "onDragstart"]))
        ], 10, _hoisted_7)
      ]),
      _: 1
    }, 8, ["items"]), [
      [_vShow, _unref(showResults)]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_8, "No search results", 512), [
      [_vShow, _unref(hasNoResults)]
    ])
  ], 512))
}
}

})